/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Browser helper
 */
export const browserHelper = {
  /**
   * @readonly
   */
  VERTICAL_DIRECTION: 'vertical',

  /**
   * @readonly
   */
  HORIZONTAL_DIRECTION: 'horizontal',

  /**
   * @readonly
   */
  BOTH_DIRECTIONS: 'both',

  /**
   * Parse raw headers to map of headers with key/value pair.
   *
   * @param {string} headers Raw response headers
   *
   * @returns {Map<string, string>} A map contains all parsed headers
   */
  parseHeaders(headers) {
    return headers
      .split(/[\r\n]/)
      .filter((header) => !!header.trim())
      .reduce((headers, header) => {
        const index = header.indexOf(': ');
        const name = header.substr(0, index).toLowerCase();
        const value = header.substr(index + 2);

        headers.set(name, value);

        return headers;
      }, new Map())
    ;
  },

  /**
   * Update current page meta data for the given `pageMetaData` object
   *
   * @param {object}   pageMetaData Page meta data object response via header
   * @param {string=}  pageMetaData.title        Page title
   * @param {string=}  pageMetaData.description  Page description
   * @param {string=}  pageMetaData.keywords     Page keywords
   * @param {object[]} pageMetaData.pageMetaTags List of custom page mete tag objects
   */
  setPageMetaData(pageMetaData) {
    if (Object.hasOwn(pageMetaData, 'title')) {
      document.title = pageMetaData.title;
    }

    for (const name of ['description', 'keywords']) {
      const metaTag = document.querySelector(`meta[name="${name}"]`);

      if (metaTag) {
        metaTag.setAttribute('content', pageMetaData[name]);
      }
    }

    pageMetaData.pageMetaTags.forEach((pageMetaTag) => {
      let metaTag;

      if (pageMetaTag.title) {
        document.title = pageMetaTag.content;
      } else if (pageMetaTag.name) {
        metaTag = document.querySelector(`meta[name="${pageMetaTag.ID}"]`);
      } else if (pageMetaTag.property) {
        metaTag = document.querySelector(`meta[property="${pageMetaTag.ID}"]`);
      }

      if (metaTag) {
        metaTag.setAttribute('content', pageMetaTag.content);
      }
    });
  },

  /**
   * Detect content overflowing for the given element
   *
   * @param {HTMLElement} element   DOM element to detect
   * @param {string}      direction Scrollbar direction to detect
   *
   * @returns {boolean}
   */
  isOverflowing(element, direction = this.VERTICAL_DIRECTION) {
    if (element instanceof HTMLBodyElement && this.isOverflowing(document.documentElement, direction)) {
      return true;
    }

    const isVertical = (direction === this.BOTH_DIRECTIONS || direction === this.VERTICAL_DIRECTION);
    const isHorizontal = (direction === this.BOTH_DIRECTIONS || direction === this.HORIZONTAL_DIRECTION);
    const isVerticalOverflowing = isVertical && element.clientHeight < element.scrollHeight;
    const isHorizontalOverflowing = isHorizontal && element.clientWidth < element.scrollWidth;

    return isVerticalOverflowing || isHorizontalOverflowing;
  },

  /**
   * Detect visibility of scrollbar for the given element.
   *
   * @param {HTMLElement} element   DOM element to detect
   * @param {string}      direction Scrollbar direction to detect
   *
   * @returns {boolean}
   */
  isScrollbarVisible(element, direction = this.VERTICAL_DIRECTION) {
    const isVertical = (direction === this.BOTH_DIRECTIONS || direction === this.VERTICAL_DIRECTION);
    const isHorizontal = (direction === this.BOTH_DIRECTIONS || direction === this.HORIZONTAL_DIRECTION);
    const {overflowX, overflowY} = getComputedStyle(element);

    if (isVertical && overflowY === 'scroll') {
      return true;
    } else if (isHorizontal && overflowX === 'scroll') {
      return true;
    } else if (this.isOverflowing(element, direction)) {
      return (isVertical && overflowY !== 'hidden') || (isHorizontal && overflowX !== 'hidden');
    }

    return false;
  },

  /**
   * Behaves the same as `auto`, but with the scrollbars drawn on top of content instead of taking up space.
   *
   * @deprecated Non-standard, only supported in some webkit-based browsers
   *
   * @param {HTMLElement} element   DOM element to detect
   * @param {string}      direction Scrollbar direction to detect
   *
   * @returns {boolean}
   */
  isOverlayScrollbar(element, direction = this.VERTICAL_DIRECTION) {
    const isVertical = (direction === this.BOTH_DIRECTIONS || direction === this.VERTICAL_DIRECTION);
    const isHorizontal = (direction === this.BOTH_DIRECTIONS || direction === this.HORIZONTAL_DIRECTION);
    const {overflowX, overflowY} = getComputedStyle(element);

    return (isVertical && overflowY === 'overlay') || (isHorizontal && overflowX === 'overlay');
  },

  /**
   * Gets browser scrollbar width. This method work with assumptions:
   *  - Horizontal and vertical scrollbars has same size.
   *  - No specific styling which affected to global scrollbars
   *
   * @returns {number} The scrollbar size in pixels
   */
  getScrollbarWidth() {
    if (!this.scrollbarWidth) {
      const div = document.createElement('div');

      div.style.position = 'fixed';
      div.style.top = '-200px';
      div.style.width = '100px';
      div.style.height = '34px';
      div.style.overflow = 'scroll';

      document.body.appendChild(div);
      this.scrollbarWidth = div.offsetWidth - div.clientWidth;
      document.body.removeChild(div);
    }

    return this.scrollbarWidth;
  },

  /**
   * Hide the page scrollbar using Bootstrap modal plugin
   */
  hideScrollbar() {
    if (!document.querySelector('.modal.show')) {
      $('body').modal('_checkScrollbar').modal('_setScrollbar');
    }
  },

  /**
   * Show the page scrollbar using Bootstrap modal plugin
   */
  showScrollbar() {
    if (!document.querySelector('.modal.show')) {
      $('body').removeClass('modal-open').modal('_resetAdjustments').modal('_resetScrollbar');
    }
  },
};
